import JSZip from "jszip";
import { MAX_TOTAL_SIZE } from "@/services/folderHierarchyService";
import { CHAT_ATTACHMENT_LIMITS, formatChatAttachmentSize } from "@hector/shared-types";
import { getFileType, getMimeType, isSupportedFileType, validateFile } from "@/utils/fileProcessing";

/**
 * Ouvre les archives .zip déposées ou choisies parmi des pièces, pour verser leur
 * contenu avec son arborescence.
 *
 * Pourquoi : sous Windows, un dossier glissé depuis l'INTÉRIEUR d'un .zip ouvert
 * dans l'Explorateur arrive à plat dans le navigateur. L'Explorateur ne sert que des
 * fichiers virtuels (FILEDESCRIPTOR / FILECONTENTS), et Chromium n'en garde que le
 * nom de base et saute les entrées dossier (`GetUniqueVirtualFilename` → `BaseName()`,
 * `GetVirtualFilenames` : « Folder entries not currently supported »,
 * ui/base/clipboard/clipboard_util_win.cc, relu le 2026-11-02). La page ne peut
 * rien reconstruire ; le seul geste qui garde les sous-dossiers est de déposer le
 * .zip lui-même. Constaté le 2026-11-02 : 43 pièces versées à plat depuis un export
 * de procédure pénale.
 */

/** Fichier porteur d'un chemin relatif, comme ceux d'un dossier glissé-déposé. */
type FileWithRelativePath = File & { readonly webkitRelativePath: string };

export function isZipFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".zip");
}

// Page de code OEM 850 (Europe de l'Ouest), octets 0x80 à 0xFF. Le compresseur
// intégré des Windows plus anciens écrit les noms dans cette page sans poser le
// drapeau UTF-8 : sans elle, « Écritures » deviendrait illisible.
const CP850_HIGH =
  "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø×ƒáíóúñÑªº¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ðÐÊËÈıÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµþÞÚÛÙýÝ¯´­±‗¾¶§÷¸°¨·¹³²■ ";

/**
 * Décode un nom d'entrée sans drapeau UTF-8 : UTF-8 s'il est valide (macOS et
 * 7-Zip l'écrivent parfois sans le drapeau), sinon CP850.
 */
export function decodeZipEntryName(bytes: string[] | Uint8Array): string {
  // Sans Uint8Array, JSZip passe un tableau d'octets typé `string[]`.
  const data = bytes instanceof Uint8Array ? bytes : Uint8Array.from(bytes, Number);
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(data);
  } catch {
    let name = "";
    for (const byte of data) {
      name += byte < 0x80 ? String.fromCharCode(byte) : CP850_HIGH[byte - 0x80];
    }
    return name;
  }
}

/** Fichiers système glissés dans les archives macOS ou Windows, jamais des pièces. */
function isSystemEntry(segments: string[]): boolean {
  return segments.some((segment) => segment === "__MACOSX" || segment.startsWith("."))
    || (segments.at(-1) ?? "").startsWith("_");
}

// Borne d'un dépôt : celle d'un lot d'import (MAX_TOTAL_SIZE), partagée par toutes
// ses archives. Mesurée en décompressant, pas lue dans l'en-tête qu'une archive peut
// falsifier : une « bombe » s'arrête au premier octet de trop au lieu de saturer la
// mémoire du navigateur. Pas de plafond du nombre de fichiers pour le dépôt de
// pièces : celui d'un dossier (2 000) ne retient que le traitement IA, jamais le
// versement. Le chat a les siens (`ZipEntryLimits`).

/**
 * Flux d'une entrée JSZip : API publique (https://stuk.github.io/jszip/documentation/api_zipobject/internal_stream.html,
 * lue le 2026-11-02) absente des types livrés par jszip 3.10.
 */
interface ZipEntryStream {
  on(event: "data", listener: (chunk: Uint8Array) => void): ZipEntryStream;
  on(event: "error", listener: (error: unknown) => void): ZipEntryStream;
  on(event: "end", listener: () => void): ZipEntryStream;
  pause(): ZipEntryStream;
  resume(): ZipEntryStream;
}

function entryStream(entry: JSZip.JSZipObject): ZipEntryStream {
  return (entry as unknown as { internalStream(type: "uint8array"): ZipEntryStream }).internalStream("uint8array");
}

/**
 * Bornes propres à un appelant. Le chat en a (50 documents de 50 Mo par message) ;
 * le dépôt de pièces n'en a pas.
 */
interface ZipEntryLimits {
  /** Fichiers à décompresser ; les autres sont rendus dans `rejected` sans être lus. */
  accepts?: (name: string) => boolean;
  /** Au-delà de ce nombre de fichiers acceptés, l'archive est refusée avant toute décompression. */
  maxEntries?: number;
  /** Au-delà, l'entrée est abandonnée en cours de décompression, sans faire échouer l'archive. */
  maxEntryBytes?: number;
}

/** Refus d'une archive pour une borne de l'appelant, dont le message dit la borne. */
class ArchiveLimitError extends Error {}

/**
 * Décompresse une entrée en flux, en débitant le budget d'octets de l'archive.
 * Rend `null` si l'entrée dépasse `maxEntryBytes` : ses octets lus sont rendus au budget.
 */
function inflateWithinBudget(
  entry: JSZip.JSZipObject,
  budget: { bytesLeft: number },
  maxEntryBytes = Number.POSITIVE_INFINITY,
): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    const chunks: Uint8Array<ArrayBuffer>[] = [];
    let entryBytes = 0;
    const stream = entryStream(entry);
    stream
      .on("data", (chunk) => {
        entryBytes += chunk.length;
        if (entryBytes > maxEntryBytes) {
          stream.pause();
          budget.bytesLeft += entryBytes - chunk.length;
          resolve(null);
          return;
        }
        budget.bytesLeft -= chunk.length;
        if (budget.bytesLeft < 0) {
          stream.pause();
          reject(new Error(`archive trop volumineuse une fois décompressée (entrée ${entry.name})`));
          return;
        }
        chunks.push(chunk.slice());
      })
      .on("error", reject)
      .on("end", () => resolve(new Blob(chunks)))
      .resume();
  });
}

function withRelativePath(file: File, relativePath: string): FileWithRelativePath {
  Object.defineProperty(file, "webkitRelativePath", { value: relativePath, writable: false });
  return file as FileWithRelativePath;
}

async function expandZip(
  zipFile: File,
  budget: { bytesLeft: number },
  limits: ZipEntryLimits = {},
): Promise<{ files: FileWithRelativePath[]; oversized: string[]; rejected: string[] }> {
  // Une archive plus lourde que le budget le dépasse aussi décompressée : refusée
  // avant d'être lue en mémoire.
  if (zipFile.size > budget.bytesLeft) throw new Error("archive plus lourde que le budget de décompression");
  // ArrayBuffer plutôt que le File : JSZip ne reconnaît pas le Blob hors navigateur.
  const archive = await JSZip.loadAsync(await zipFile.arrayBuffer(), { decodeFileName: decodeZipEntryName });
  const entries = Object.values(archive.files)
    .filter((entry) => !entry.dir)
    .map((entry) => ({ entry, segments: entry.name.split(/[\\/]/).filter(Boolean) }))
    .filter(({ segments }) => segments.length > 0 && !isSystemEntry(segments));
  const accepts = limits.accepts ?? (() => true);
  const accepted = entries.filter(({ segments }) => accepts(segments.at(-1) ?? ""));
  if (limits.maxEntries !== undefined && accepted.length > limits.maxEntries) {
    throw new ArchiveLimitError(
      `archive de plus de ${limits.maxEntries} fichiers, le maximum d’un message : joignez-en les fichiers utiles, ou versez-la dans un dossier`,
    );
  }

  // « Envoyer vers > Dossier compressé » sur LAPORTE produit LAPORTE.zip qui contient
  // LAPORTE/… : ce dossier unique sert de racine. Sinon le nom de l'archive en tient
  // lieu, comme pour un dossier déposé.
  const topLevel = new Set(entries.map(({ segments }) => segments[0]));
  const singleRootFolder = topLevel.size === 1 && entries.every(({ segments }) => segments.length > 1);
  const root = singleRootFolder ? [] : [zipFile.name.replace(/\.zip$/i, "")];

  // Un .zip trouvé dans un dossier déposé garde la place de ce dossier.
  const zipParent = (zipFile as FileWithRelativePath).webkitRelativePath?.split("/").slice(0, -1) ?? [];

  // Une entrée après l'autre : le budget s'arrête au premier dépassement, et une seule
  // décompression occupe la mémoire à la fois.
  const files: FileWithRelativePath[] = [];
  const oversized: string[] = [];
  const rejected: string[] = [];
  for (const { entry, segments } of entries) {
    const name = segments.at(-1) ?? entry.name;
    const relativePath = [...zipParent, ...root, ...segments].join("/");
    if (!accepts(name)) {
      rejected.push(relativePath);
      continue;
    }
    const blob = await inflateWithinBudget(entry, budget, limits.maxEntryBytes);
    if (blob === null) {
      oversized.push(relativePath);
      continue;
    }
    // Le type MIME suit l'extension : sans lui, le multipart part en
    // application/octet-stream et la pièce est enregistrée sous ce type.
    const file = new File([blob], name, { type: getMimeType(getFileType(name)), lastModified: entry.date.getTime() });
    files.push(withRelativePath(file, relativePath));
  }
  return { files, oversized, rejected };
}

/**
 * Remplace chaque .zip par son contenu, chaque fichier portant son chemin dans
 * `webkitRelativePath`. Une archive impossible à ouvrir (endommagée, chiffrée, hors
 * bornes) reste dans la liste telle quelle : la validation la refuse avec sa raison.
 */
export async function expandZipArchives<T extends File>(
  files: T[],
  maxBytes: number = MAX_TOTAL_SIZE,
): Promise<Array<T | FileWithRelativePath>> {
  const budget = { bytesLeft: maxBytes };
  const expanded: Array<T | FileWithRelativePath> = [];
  for (const file of files) {
    if (!isZipFile(file)) {
      expanded.push(file);
      continue;
    }
    try {
      expanded.push(...(await expandZip(file, budget)).files);
    } catch (error) {
      console.warn("[expandZipArchives] archive illisible, refusée à la validation", {
        fileName: file.name,
        size: file.size,
        message: error instanceof Error ? error.message : String(error),
      });
      expanded.push(file);
    }
  }
  return expanded;
}

/** Raison affichée pour un .zip resté entier : seule une archive illisible l'est. */
export const UNREADABLE_ZIP_REASON =
  "Archive .zip impossible à ouvrir (endommagée, protégée par mot de passe, ou plus de 1 Go une fois décompressée) : décompressez-la puis déposez son contenu";

/** Fichier d'une archive que le composeur du chat ne joint pas, avec sa raison. */
export interface SkippedArchiveEntry {
  name: string;
  reason: string;
}

/**
 * Ouvre les .zip joints à un message de chat. Chaque fichier extrait est nommé
 * d'après son chemin joint par « › » : multer réduit au nom de base tout nom qui
 * porte des « / » (busboy, `preservePath: false`), et ce nom est le seul contexte
 * de dossier que l'agent reçoit. Les bornes d'un message (50 documents, 50 Mo par
 * fichier) s'appliquent pendant l'ouverture, pas après : un format non pris en charge
 * n'est pas décompressé, une archive de plus de 50 fichiers pris en charge est refusée
 * avant décompression, un fichier trop lourd est abandonné dès son 50e Mo. Fichiers refusés et archive illisible sont écartés dans `skipped` ;
 * le reste du lot passe tel quel.
 */
export async function expandChatAttachmentArchives(
  files: File[],
): Promise<{ files: File[]; skipped: SkippedArchiveEntry[] }> {
  const budget = { bytesLeft: MAX_TOTAL_SIZE };
  const limits = {
    accepts: (name: string) => isSupportedFileType(getFileType(name)),
    maxEntries: CHAT_ATTACHMENT_LIMITS.maxFilesPerMessage,
    maxEntryBytes: CHAT_ATTACHMENT_LIMITS.maxFileSizeBytes,
  };
  const oversizedReason = `Fichier trop volumineux : plus de ${formatChatAttachmentSize(limits.maxEntryBytes)}. `
    + `Maximum autorisé : ${formatChatAttachmentSize(limits.maxEntryBytes)}.`;
  const displayName = (relativePath: string) => relativePath.replaceAll("/", " › ");
  const kept: File[] = [];
  const skipped: SkippedArchiveEntry[] = [];
  for (const file of files) {
    if (!isZipFile(file)) {
      kept.push(file);
      continue;
    }
    let opened: Awaited<ReturnType<typeof expandZip>>;
    try {
      opened = await expandZip(file, budget, limits);
    } catch (error) {
      skipped.push({ name: file.name, reason: error instanceof ArchiveLimitError ? error.message : UNREADABLE_ZIP_REASON });
      continue;
    }
    for (const path of opened.oversized) skipped.push({ name: displayName(path), reason: oversizedReason });
    for (const path of opened.rejected) skipped.push({ name: displayName(path), reason: "format non pris en charge" });
    for (const extracted of opened.files) {
      const name = displayName(extracted.webkitRelativePath);
      const validation = validateFile(extracted);
      if (!validation.isValid) {
        skipped.push({ name, reason: validation.error ?? "" });
        continue;
      }
      kept.push(new File([extracted], name, { type: extracted.type, lastModified: extracted.lastModified }));
    }
  }
  return { files: kept, skipped };
}
